// src/app/store.ts
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

// Import reducers
import courseReducer from "../reducers/courses/courseSlice";
import authReducer from "../reducers/auth/authSlice";
import taskReducer from "../reducers/task/taskSlice";
import resourcesReducer from "../reducers/resources/resourceSlice";
import templatesReducer from "../reducers/qa/qaSlice";
import testReducer from "../reducers/test/testSlice";
import scheduleReducer from "../reducers/schedule/scheduleSlice";
import organizationReducer from "../reducers/organization/organizationSlice";
import userSlice from "../reducers/users/usersSlice";
import departmentSlice from "../reducers/departments/departmentSlice";
// Import the role change middleware
import { roleChangeMiddleware } from "./boostrap";

// Step 1: Combine reducers
const appReducer = combineReducers({
  courses: courseReducer,
  auth: authReducer,
  tasks: taskReducer,
  resources: resourcesReducer,
  templates: templatesReducer,
  test: testReducer,
  schedules: scheduleReducer,
  organizations: organizationReducer,
  users: userSlice,
  departments: departmentSlice,
});

// Step 2: Wrap rootReducer to allow reset on logout
const rootReducer = (state: any, action: any) => {
  if (action.type === "auth/logout") {
    // Reset everything on logout
    state = undefined;
  }
  return appReducer(state, action);
};

// Step 3: Persist config
const persistConfig = {
  key: "root",
  storage,
  version: 1,
  whitelist: [
    "auth", 
  ],
  blacklist: [
    "tasks",
    "test", 
    "schedules",
    "templates",
    "resources",
    "courses",
    "users",
    "organizations", // These should be refetched based on role
    "departments",   // These should be refetched based on role
  ],
};

// Step 4: Persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Step 5: Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "auth/login/fulfilled",
          "auth/login/rejected",
        ],
        ignoredPaths: ["auth.error"],
      },
    }).concat(roleChangeMiddleware), // Use the role change middleware instead
  devTools: process.env.NODE_ENV !== "production",
});

// Persistor
export const persistor = persistStore(store);

// Types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;